<script setup lang="ts">
  import {
    Dropdown,
    DropdownItem,
    FontIcon,
    TextInput,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, useId } from 'vue';
  import {
    pageSizeSelectorClass,
    pageSizeSelectorLabelClass,
    pageSizeSelectorTriggerClass,
  } from './DataTablePageSizeSelector.css.ts';

  export type DataTablePageSizeSelectorProps = {
    rowsPerPage?: number;
    itemsPerPageOptions?: number[];
    loading?: boolean;
  };

  const { t } = useLocale('table');

  const props = defineProps<DataTablePageSizeSelectorProps>();

  const emits = defineEmits<{
    setPageSize: [value: string | number];
  }>();

  const rowsPerPageId = useId();

  const selectedValue = computed(() =>
    props.rowsPerPage !== undefined ? props.rowsPerPage.toString() : null,
  );

  const onSelect = (value: string) => {
    emits('setPageSize', value);
  };
</script>

<template>
  <div :class="pageSizeSelectorClass" id="pageSizeSelector">
    <label :for="rowsPerPageId" :class="pageSizeSelectorLabelClass">{{
      t('table.rows_per_page')
    }}</label>
    <Dropdown
      :model-value="selectedValue"
      :disabled="loading"
      @update:model-value="onSelect"
    >
      <template #trigger="{ displayValue }">
        <TextInput
          :id="rowsPerPageId"
          read-only
          :model-value="displayValue"
          :disabled="loading"
          :class="pageSizeSelectorTriggerClass"
        >
          <template #icon>
            <FontIcon :icon-name="TUNDR_ICONS.CHEVRON_DOWN" />
          </template>
        </TextInput>
      </template>
      <DropdownItem
        v-for="option in itemsPerPageOptions"
        :key="option"
        :label="option.toString()"
        :value="option.toString()"
      />
    </Dropdown>
  </div>
</template>
